/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {useAppDispatch, useAppSelector} from "../store";
import {Button, Drawer, Form, Input, Menu, Modal, Select, Space, Tooltip} from "antd";
import {useEffect, useState} from "react";
import {closeDrawer, openDrawer} from "../reducers/drawers-reducer";
import {triggerReload} from "../reducers/reloadsignal-reducer";
import {patch, post, del, get} from "../core/request";
import {IconSet} from "../components/icons";
import {Dropdown} from "antd";
import {CustomButton} from "../components/buttons";

import {Customer, Person} from "../core/datamodel";
import {useHistory} from "react-router-dom";
import TextArea from "antd/es/input/TextArea";
import CustomSelect from "../components/select";

type Class = Customer
const stack_selector = (state: any) => state.drawer.customer
const uri = "customers"
const key = "customer"
const title = "Kunde"

interface DrawerProps {
    size?: 'default' | 'large'
}


export function CustomerDrawer(props: DrawerProps) {
    const history = useHistory();
    const stack = useAppSelector(stack_selector)
    const drawer_stack = useAppSelector(state => state.drawer.stack)
    const dispatch = useAppDispatch();
    const [object, setObject] = useState({} as Class);
    const [form] = Form.useForm();

    const personReloadSignal = useAppSelector(state => state.reloadSignals.person)
    const [persons, setPersons] = useState([])

    const userReloadSignal = useAppSelector(state => state.reloadSignals.user)
    const [users, setUsers] = useState([])
    const [permissions, setPermissions] = useState({} as any);

    const isNew = () => !(!!object?.props?.uuid)

    useEffect(() => {
        get("persons").then(
            (response) => setPersons(response.data.persons),
            (e) => console.error(e)
        )
    }, [stack, personReloadSignal])

    useEffect(() => {
        get("users").then(
            (response) => setUsers(response.data.users.filter(
                    (mpe: any) => mpe?.groups?.some(
                        (group: string) => group === 'mpe')
                )
            ), (e) => console.error(e)
        )
    }, [stack, userReloadSignal])


    useEffect(() => {
        const object = stack[stack.length - 1] as Class
        form.resetFields()
        form.setFieldsValue({
            ...object?.props,
            contact_ceo: object?.props?.contact_ceo ? object?.props?.contact_ceo.props.uuid : undefined,
            contact_it: object?.props?.contact_it ? object?.props?.contact_it.props.uuid : undefined,
            contact_invoice: object?.props?.contact_invoice ? object?.props?.contact_invoice.props.uuid : undefined,
            contact_radiation_protection_officer: object?.props?.contact_radiation_protection_officer ?
                object?.props?.contact_radiation_protection_officer.props.uuid : undefined,
            mpe: object?.props?.mpe ? object?.props?.mpe.props.uuid : undefined,

        })
        setObject(object)
    }, [stack, form])

    useEffect(() => {
        get("customers/" + object?.props?.uuid + "/permissions").done((response) => {
                setPermissions(response.data.permissions);
            }
        )
    }, [object?.props?.uuid])


    const onFormFinish = () => {
        let values = form.getFieldsValue()
        object.update({
            ...values,
            contact_ceo: values.contact_ceo ? new Person({'uuid': values.contact_ceo}) : null,
            contact_it: values.contact_it ? new Person({'uuid': values.contact_it}) : null,
            contact_invoice: values.contact_invoice ? new Person({'uuid': values.contact_invoice}) : null,
            contact_radiation_protection_officer: values.contact_radiation_protection_officer ?
                new Person({'uuid': values.contact_radiation_protection_officer}) : null,
            mpe: values.mpe ? new Person({'uuid': values.mpe}) : null,
        })

        let req: JQueryPromise<any>;
        if (isNew()) {
            req = post(uri, object.dump())
        } else {
            req = patch(uri + "/" + object.props.uuid, object.dump())
        }

        req.then(() => {
                dispatch(triggerReload(key))
                dispatch(closeDrawer(key))
            },
            (e) => {
                console.error(e);
            })
    }

    const onDisable = () => {
        object.update({'disabled': !object.props.disabled})
        patch(uri + "/" + object.props.uuid,
            {
                'uuid': object.props.uuid,
                'disabled': object.props.disabled
            }
        ).then(
            () => {
                dispatch(triggerReload(key))
                dispatch(closeDrawer(key))
            },
            (e) => {
                console.error(e);
            })
    }

    const onDelete = () => {
        Modal.confirm({
            title: "Eintrag wirklich gelöscht werden?",
            content: "Diese Aktion kann nicht rückgängig gemacht werden!",
            maskClosable: true,
            cancelText: 'Abbrechen',
            autoFocusButton: 'cancel',
            okText: 'Löschen',
            okType: 'danger',
            onOk: () => {
                del(uri + "/" + object.props.uuid)
                    .then(
                        () => {
                            history.goBack()
                            dispatch(triggerReload(key))
                            dispatch(closeDrawer(key))
                        },
                        (e) => {
                            console.error(e);
                        })
                return false;
            }
        })
    }

    const onDrawerClose = (e: any) => {
        if (e.type === 'click' &&
            (e.target as Element).className === "ant-drawer-mask"
        ) {
            Modal.confirm({
                title: "Fenster wirklich schließen?",
                content: "Alle Änderungen gehen verloren!",
                maskClosable: true,
                cancelText: 'Weiter bearbeiten',
                okText: 'Änderungen verwerfen',
                autoFocusButton: 'cancel',
                onOk: () => {
                    dispatch(closeDrawer(key));
                    return false;
                }
            })
        } else {
            dispatch(closeDrawer(key))
        }

    }

    const form_layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 16},
    };

    const personOptions = (
        <>
            {
                persons.map(
                    (person: { [key: string]: string }) =>
                        <Select.Option
                            key={person.uuid}
                            value={person.uuid}
                        >
                            {person.repr}
                        </Select.Option>)
            }
        </>
    )

    const mpeOptions = (
        <>
            {
                users.map(
                    (user: { person: { uuid: string, repr: string } }) => {
                        return <Select.Option
                            key={user.person.uuid}
                            value={user.person.uuid}
                        >
                            {user.person.repr}
                        </Select.Option>
                    }
                )
            }
        </>
    )

    const form_element = (
        <Form
            form={form}
            onFinish={onFormFinish}
            {...form_layout}
            requiredMark={true}
        >
            <Form.Item name="name" label="Kundenname" required
                       rules={[{required: true, message: "Bitte Kundennamen eingeben!"}]}
                       tooltip="Kundenname wie er in der Postanschrift verwendet wird"
            >
                <Input name='name' placeholder="Kundenname"/>
            </Form.Item>

            <Form.Item name="alias"
                       label="Alias"
                       tooltip="Ersetzt den Kundennamen durch ein Alias in Ansichten von [ MPExpert ]"
            >
                <Input name='alias' placeholder="Alias"/>
            </Form.Item>

            <Form.Item
                label="Rechnungsadresse"
                style={{marginBottom: 0}}
            >
                <Form.Item
                    name="street"
                    style={{display: 'inline-block', width: 'calc(50% - 4px)'}}
                >
                    <Input name='street' placeholder="Straße"/>
                </Form.Item>
                <Form.Item
                    name="house_nr"
                    style={{display: 'inline-block', width: 'calc(50% - 4px)', margin: '0 0 0 8px'}}>
                    <Input name="house_nr" placeholder="Hausnummer"/>
                </Form.Item>
                <Form.Item
                    name="additional_adresse"
                    style={{display: 'inline-block', width: '100%'}}
                >
                    <Input name='additional_adresse' placeholder="Addresszusatz"/>
                </Form.Item>
                <Form.Item
                    name="postcode"
                    style={{display: 'inline-block', width: 'calc(50% - 4px)'}}
                >
                    <Input name='postcode' placeholder="PLZ"/>
                </Form.Item>
                <Form.Item
                    name="city"
                    style={{display: 'inline-block', width: 'calc(50% - 4px)', margin: '0 0 0 8px'}}>
                    <Input name="city" placeholder="Stadt"/>
                </Form.Item>
            </Form.Item>

            <Form.Item label="CEO" style={{margin: 0}}>
                <Form.Item style={{display: 'inline-block', width: 'calc(100% - 32px - 8px)'}} name="contact_ceo">
                    <CustomSelect searchable placeholder="CEO" allowClear loading={persons.length === 0}>
                        {personOptions}
                    </CustomSelect>
                </Form.Item>
                <Form.Item style={{display: 'inline-block', margin: '0 0 0 8px'}}>
                    <CustomButton mode='add'
                                  size="small"                                  tooltip="Neue Person hinzufügen"
                                  onClick={
                                      () => dispatch(openDrawer(
                                          {key: 'person', object: new Person({})}
                                      ))
                                  }/>
                </Form.Item>
            </Form.Item>

            <Form.Item label="Rechnungskontakt" style={{margin: 0}}>
                <Form.Item style={{display: 'inline-block', width: 'calc(100% - 32px - 8px)'}} name="contact_invoice">
                    <CustomSelect searchable placeholder="Rechnungsempfänger, wenn leer wird CEO verwendet" allowClear
                            loading={persons.length === 0}>
                        {personOptions}
                    </CustomSelect>
                </Form.Item>
                <Form.Item style={{display: 'inline-block', margin: '0 0 0 8px'}}>
                    <CustomButton mode='add'
                                  size="small"                                  tooltip="Neue Person hinzufügen"
                                  onClick={
                                      () => dispatch(openDrawer(
                                          {key: 'person', object: new Person({})}
                                      ))
                                  }/>
                </Form.Item>
            </Form.Item>

            <Form.Item label="IT" style={{margin: 0}}>
                <Form.Item style={{display: 'inline-block', width: 'calc(100% - 32px - 8px)'}} name="contact_it">
                    <CustomSelect searchable placeholder="Ansprechpartner IT" allowClear loading={persons.length === 0}>
                        {personOptions}
                    </CustomSelect>
                </Form.Item>
                <Form.Item style={{display: 'inline-block', margin: '0 0 0 8px'}}>
                    <CustomButton mode='add'
                                  size="small"                                  tooltip="Neue Person hinzufügen"
                                  onClick={
                                      () => dispatch(openDrawer(
                                          {key: 'person', object: new Person({})}
                                      ))
                                  }/>
                </Form.Item>
            </Form.Item>

            <Form.Item label="SSV" style={{margin: 0}} tooltip={"Strahlenschutzverantwortlicher"} required>
                <Form.Item style={{display: 'inline-block', width: 'calc(100% - 32px - 8px)'}}
                           name="contact_radiation_protection_officer"
                           required
                           rules={[{required: true, message: "Bitte Strahlenschutzverantwortlichen wählen!"}]}>
                    <CustomSelect searchable placeholder="Strahlenschutzverantwortlicher" allowClear loading={persons.length === 0}>
                        {personOptions}
                    </CustomSelect>
                </Form.Item>
                <Form.Item style={{display: 'inline-block', margin: '0 0 0 8px'}}>
                    <CustomButton mode='add'
                                  size="small"
                                  tooltip="Neue Person hinzufügen"
                                  onClick={
                                      () => dispatch(openDrawer(
                                          {key: 'person', object: new Person({})}
                                      ))
                                  }/>
                </Form.Item>
            </Form.Item>

            <Form.Item label="Synology-Link" name="synologylink">
                <Input name="synologylink" placeholder="Synology-Link"/>
            </Form.Item>

            <Form.Item label="Kommentar" name="comment">
                <TextArea allowClear name="comment" placeholder="Platz für Notizen" maxLength={2048}/>
            </Form.Item>

            <Form.Item label="MPE" name="mpe" required rules={[{required: true, message: "Bitte MPE wählen!"}]}>
                <CustomSelect searchable placeholder="Verantwortlicher MPE" loading={users.length === 0}>
                    {mpeOptions}
                </CustomSelect>
            </Form.Item>


            {/* Buttons */}
            <Form.Item
                label=" "
                colon={false}
            >
                <Form.Item
                    style={{display: 'inline-block'}}
                >
                    <Button type="primary" htmlType="submit">
                        <IconSet type='save'/>&nbsp;Speichern
                    </Button>
                </Form.Item>
                <Form.Item
                    style={{display: 'inline-block', margin: '0 24px'}}
                >
                    <Button onClick={onDrawerClose}>
                        <IconSet type='cancel'/>&nbsp;Änderungen verwerfen
                    </Button>
                </Form.Item>
            </Form.Item>
        </Form>
    )

    const menu = (
        isNew() ? (<></>) : (
            <Dropdown
                arrow
                overlay={
                    <Menu style={{padding: 12}} items={[
                        {
                            key: "disable",
                            onClick: onDisable,
                            icon: <IconSet type='disable'/>,
                            label: !!object?.props?.disabled ? ("Reaktivieren") : ("Deaktivieren")
                        },
                        {
                            key: "delete",
                            danger: true,
                            disabled: !(object?.props?.is_deletable && permissions?.customer?.delete),
                            onClick: onDelete,
                            icon: <IconSet type='delete'/>,
                            label: "Löschen"
                        },
                    ]}
                    />
                }
                placement="bottomRight"
                className='button-edit'
            >
                <CustomButton mode='more'/>
            </Dropdown>)
    )

    return (
        <Drawer
            open={stack.length > 0 && drawer_stack[drawer_stack.length - 1] === key}
            size={props.size}
            onClose={onDrawerClose}
            forceRender={true}
            title={
                <Space direction="horizontal" style={{width: '100%', justifyContent: 'center'}}>
                    <h1 style={{marginBottom: 0}}>{title + (isNew() ? " hinzufügen" : " bearbeiten")}</h1>
                </Space>
            }
            extra={menu}
            zIndex={drawer_stack[drawer_stack.length - 1] === key ? 1000 : 900}
        >
            {form_element}
        </Drawer>
    )
}


CustomerDrawer.defaultProps = {
    size: 'large'
}